import React, { ReactNode, useRef } from 'react'
import { Stack, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material'
import { useTableColumnLayout } from '../../hooks/useTableColumnLayout'
import { DataTableHeaderCell, DataTablePagination } from './cells'
import { DataTableRow } from './DataTableRow'
import { DataTableReorderContext } from './DataTableReorderContext'
import { SortableDataTableRow } from './SortableDataTableRow'
import {
    DataTableCellRenderer,
    DataTableColumn,
    DataTableReorder,
    DataTableSortKey,
    DataTableVariant,
} from './DataTable.types'
import { useHorizontalOverflow } from '../../hooks/useHorizontalOverflow'
import { useIsMobileDevice } from '../../hooks/useIsMobileDevice'
import { getStickyColumns } from './DataTable.sticky'
import { DATA_TABLE_INDEX_COLUMN_WIDTH, DATA_TABLE_VARIANTS } from './DataTable.variants'
import { useDataTable } from './hooks/useDataTable'
import { useDataTableReorder } from './hooks/useDataTableReorder'
import { DataTableRowsPerPage } from './hooks/useDataTableRowsPerPage'
import { DataTableSort } from './hooks/useDataTableSort'
import { thinScrollbarSx } from '../../../style/scrollbar'

export type DataTableProps<TRow, TColumnId extends string> = {
    columns: DataTableColumn<TRow, TColumnId>[]
    rows: TRow[]
    getRowId: (row: TRow) => string
    renderCell: DataTableCellRenderer<TRow, TColumnId>
    renderRowActions?: (row: TRow) => ReactNode
    variant?: DataTableVariant
    showIndexColumn?: boolean
    toolbar?: ReactNode
    emptyState?: ReactNode
    expandedRowId?: string | null
    renderExpandedContent?: (row: TRow) => ReactNode
    minTableWidth?: number
    sort?: DataTableSort<TColumnId>
    rowsPerPageState?: DataTableRowsPerPage
    pinnedSort?: DataTableSortKey<TColumnId>
    defaultSortColumnId?: TColumnId
    hiddenColumnIds?: readonly string[]
    /** Pins the index and first columns to the left and the actions column to the right. */
    hasStickyColumns?: boolean
    reorder?: DataTableReorder<TRow>
}

export const DataTable = <TRow, TColumnId extends string>({
    columns,
    rows,
    getRowId,
    renderCell,
    renderRowActions,
    variant = 'primary',
    showIndexColumn = false,
    toolbar,
    emptyState,
    expandedRowId = null,
    renderExpandedContent,
    minTableWidth,
    sort,
    rowsPerPageState,
    pinnedSort,
    defaultSortColumnId,
    hiddenColumnIds,
    hasStickyColumns = false,
    reorder,
}: DataTableProps<TRow, TColumnId>) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const { hasLeftOverflow, hasRightOverflow, visibleWidth } = useHorizontalOverflow(scrollRef)
    const isMobileDevice = useIsMobileDevice()

    const variantConfig = DATA_TABLE_VARIANTS[variant]
    const { actionsColumnWidth, headerTypographyVariant, cellPaddingX } = variantConfig

    const hasActionsColumn = !!renderRowActions
    const indexColumnWidth = showIndexColumn ? DATA_TABLE_INDEX_COLUMN_WIDTH : 0
    const reservedWidth = indexColumnWidth + (hasActionsColumn ? actionsColumnWidth : 0)

    const {
        sortKey,
        handleSort,
        isInStoredOrder,
        page,
        rowsPerPage,
        handlePageChange,
        handleRowsPerPageChange,
        isPaginationVisible,
        sortedRows,
        paginatedRows,
    } = useDataTable<TRow, TColumnId>({
        rows,
        columns,
        sort,
        rowsPerPageState,
        pinnedSort,
        defaultSortColumnId,
        hiddenColumnIds,
        isSortClearable: !!reorder,
    })

    const { isReorderEnabled, handleDragEnd } = useDataTableReorder({
        reorder,
        rows: sortedRows,
        getRowId,
        isInStoredOrder,
        rowCountOnPage: paginatedRows.length,
        hasExpandedRow: paginatedRows.some((row) => getRowId(row) === expandedRowId),
    })

    const { visibleColumns, tableMinWidth } = useTableColumnLayout<DataTableColumn<TRow, TColumnId>>({
        columns,
        hiddenColumnIds,
        minTableWidth: minTableWidth ?? variantConfig.minTableWidth,
        reservedWidth,
        availableWidth: visibleWidth,
    })

    const columnCount = visibleColumns.length + (showIndexColumn ? 1 : 0) + (hasActionsColumn ? 1 : 0)

    const stickyColumns = getStickyColumns({
        isEnabled: hasStickyColumns,
        showIndexColumn,
        indexColumnWidth,
        hasActionsColumn,
        pinsFirstColumn: !isMobileDevice,
        hasLeftOverflow,
        hasRightOverflow,
    })

    if (!rows.length && emptyState) {
        return (
            <Stack gap={2} minWidth={0}>
                {toolbar}
                {emptyState}
            </Stack>
        )
    }

    const canReorder = !!reorder

    const renderedRows = paginatedRows.map((row, rowIndex) => {
        const rowId = getRowId(row)

        const rowProps = {
            row,
            rowId,
            rowNumber: page * rowsPerPage + rowIndex + 1,
            isFirstRow: rowIndex === 0,
            isLastRow: rowIndex === paginatedRows.length - 1,
            isExpanded: expandedRowId === rowId,
            columns: visibleColumns,
            columnCount,
            variantConfig,
            indexColumnWidth,
            renderCell,
            renderRowActions,
            renderExpandedContent,
            stickyColumns,
            stickyContentWidth: hasStickyColumns ? visibleWidth : 0,
        }

        return canReorder ? (
            <SortableDataTableRow key={rowId} {...rowProps} isDragDisabled={!isReorderEnabled} />
        ) : (
            <DataTableRow key={rowId} {...rowProps} />
        )
    })

    const table = (
        <TableContainer ref={scrollRef} sx={{ overflowX: 'auto', width: '100%', maxWidth: '100%', ...thinScrollbarSx }}>
            <Table
                size="small"
                sx={{
                    minWidth: tableMinWidth,
                    width: '100%',
                    tableLayout: 'fixed',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                }}
            >
                <TableHead>
                    <TableRow>
                        {showIndexColumn && (
                            <DataTableHeaderCell width={`${indexColumnWidth}px`} sticky={stickyColumns.index} />
                        )}
                        {visibleColumns.map((column, columnIndex) => (
                            <DataTableHeaderCell
                                key={column.id}
                                sticky={columnIndex === 0 ? stickyColumns.firstColumn : undefined}
                                tooltip={column.tooltip}
                                width={column.width}
                                align={column.align}
                                paddingX={cellPaddingX}
                                typographyVariant={headerTypographyVariant}
                                isSortable={!!column.getSortValue && column.id !== pinnedSort?.columnId}
                                isSortActive={sortKey?.columnId === column.id}
                                sortDirection={sortKey?.columnId === column.id ? sortKey.direction : 'asc'}
                                onSort={() => handleSort(column.id)}
                            >
                                {column.label}
                            </DataTableHeaderCell>
                        ))}
                        {hasActionsColumn && (
                            <DataTableHeaderCell
                                width={`${actionsColumnWidth}px`}
                                paddingX={cellPaddingX}
                                sticky={stickyColumns.actions}
                            />
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>{renderedRows}</TableBody>
            </Table>
        </TableContainer>
    )

    return (
        <Stack gap={2} minWidth={0}>
            {toolbar}
            {canReorder ? (
                <DataTableReorderContext
                    rowIds={paginatedRows.map(getRowId)}
                    onDragEnd={handleDragEnd}
                    isDisabled={!isReorderEnabled}
                >
                    {table}
                </DataTableReorderContext>
            ) : (
                table
            )}
            {isPaginationVisible && (
                <DataTablePagination
                    count={rows.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            )}
        </Stack>
    )
}

export default DataTable
